import {removeInputArrayIdentifiersFromAnotherArray} from "helpers/array";
import {removeDuplicatesFromArray} from "helpers/filterHandlers";
import {useAppSelector} from "hooks";
import useFetchListOptions from "hooks/useFetchListOptions";
import {useCallback, useMemo, useState} from "react";
import {flattenCountriesPreCalculatedSelector} from "store/filter/filterSelectors";
import {getCountries, getCountriesPreCalculated} from "store/search/searchAsyncActions";
import {
	countryOptionsPreCalculatedSelector,
	countryOptionsSelector,
} from "store/search/searchSelectors";
import useDebouncedEffect from "use-debounced-effect";

import {
	collapseDescendants,
	getCountryIdsFromContinent,
	getFullDataObjects,
	getLocationExpandableData,
	hasContinentSelectedHelper,
	isCitySelected,
	isContinentFullySelectedHelper,
	isCountryPartiallySelected,
	isCountrySelected,
	isRegionPartiallySelected,
	isRegionSelected,
	isStatePartiallySelected,
	isStateSelected,
	TExpandedState,
} from "../helpers";
import {TCity, TContinent, TCountry, TRegion, TState} from "../types";

export type UseLocationFilterProps = {
	filterType?:
		| "companies"
		| "candidates"
		| "ap_candidates"
		| "apr_candidates"
		| "project_insights";
	selectedLocation: number[];
	selectedCountryRegions: number[];
	selectedRegionStates: number[];
	selectedStateCities: number[];
	handleBulkUpdate: (ids: number[]) => void;
	handleRegionChange?: (regionId: number, country: TCountry) => void;
	handleStateChange?: (stateId: number, region: TRegion, country: TCountry) => void;
	handleCityChange?: (cityId: number, state: TState, region: TRegion, country: TCountry) => void;
	allSelectedCountriesCount: number;
};

export const useLocationFilter = ({
	filterType = "candidates",
	selectedLocation,
	selectedCountryRegions,
	selectedRegionStates,
	selectedStateCities,
	handleBulkUpdate,
	handleRegionChange,
	handleStateChange,
	handleCityChange,
	allSelectedCountriesCount,
}: UseLocationFilterProps) => {
	// Selectors
	const countryOptions = useAppSelector(countryOptionsSelector);
	const countryOptionsPreCalculated = useAppSelector(countryOptionsPreCalculatedSelector);
	const flattenCountries = useAppSelector(flattenCountriesPreCalculatedSelector);

	// Fetch data
	const {filteredData, onFilterChanged, searchTerm, loading} = useFetchListOptions({
		options:
			filterType === "candidates" || filterType === "ap_candidates"
				? countryOptionsPreCalculated
				: countryOptions,
		callbackMethod:
			filterType === "candidates" || filterType === "ap_candidates"
				? getCountriesPreCalculated
				: getCountries,
		childFilterKey: "countries",
		nestedChildFilterKey: "regions",
	});

	// State
	const [expanded, setExpanded] = useState<TExpandedState>({});

	// Derived values
	const shouldRenderCountryRegions = useMemo(
		() => filterType === "candidates" || filterType === "ap_candidates",
		[filterType],
	);

	const shouldRenderTags = useMemo(
		() =>
			(filterType === "candidates" || filterType === "ap_candidates") &&
			allSelectedCountriesCount !== 0,
		[filterType, allSelectedCountriesCount],
	);

	// Effects
	useDebouncedEffect(
		() => {
			if (!searchTerm) {
				setExpanded({});

				return;
			}
			const expandableFilteredData = getLocationExpandableData(filteredData as TContinent[]);
			setExpanded(expandableFilteredData);
		},
		500,
		[searchTerm],
	);

	// Selection helpers
	const countryIdsByContinent = useCallback((continent: TContinent) => {
		return getCountryIdsFromContinent(continent);
	}, []);

	const hasContinentSelected = useCallback(
		(continent: TContinent) => hasContinentSelectedHelper(continent, selectedLocation),
		[selectedLocation],
	);

	const isContinentFullySelected = useCallback(
		(continent: TContinent) => isContinentFullySelectedHelper(continent, selectedLocation),
		[selectedLocation],
	);

	const hasChildren = useCallback(
		(item: TCountry | TRegion | TState) => {
			if ("regions" in item) {
				if (shouldRenderCountryRegions && item.regions && item.regions.length > 0) {
					return true;
				}
				if ("states" in item && item.states && item.states.length > 0) {
					return true;
				}
				if ("cities" in item && item.cities && item.cities.length > 0) {
					return true;
				}

				return false;
			}
			if ("states" in item) return item.states !== undefined && item.states.length > 0;
			if ("cities" in item) return item.cities !== undefined && item.cities.length > 0;

			return false;
		},
		[shouldRenderCountryRegions],
	);

	// Selection check callbacks
	const isCountryPartiallySelectedCb = useCallback(
		(country: TCountry) =>
			isCountryPartiallySelected(
				country,
				selectedLocation,
				selectedCountryRegions,
				selectedRegionStates,
				selectedStateCities,
			),
		[selectedLocation, selectedCountryRegions, selectedRegionStates, selectedStateCities],
	);

	const isCountrySelectedCb = useCallback(
		(countryId: number) => isCountrySelected(countryId, selectedLocation),
		[selectedLocation],
	);

	const isRegionPartiallySelectedCb = useCallback(
		(region: TRegion, country: TCountry) => {
			const {fullRegion} = getFullDataObjects(
				country,
				region,
				{id: 0, name: "", cities: []} as TState,
				flattenCountries as TCountry[],
			);

			return isRegionPartiallySelected(
				fullRegion,
				country,
				selectedCountryRegions,
				selectedRegionStates,
				selectedStateCities,
				selectedLocation,
			);
		},
		[
			selectedCountryRegions,
			selectedRegionStates,
			selectedStateCities,
			selectedLocation,
			flattenCountries,
		],
	);

	const isStatePartiallySelectedCb = useCallback(
		(state: TState, region: TRegion, country: TCountry) => {
			const {fullState} = getFullDataObjects(
				country,
				region,
				state,
				flattenCountries as TCountry[],
			);

			return isStatePartiallySelected(
				fullState,
				region,
				country,
				selectedStateCities,
				selectedRegionStates,
				selectedCountryRegions,
				selectedLocation,
			);
		},
		[
			selectedStateCities,
			selectedRegionStates,
			selectedCountryRegions,
			selectedLocation,
			flattenCountries,
		],
	);

	const isCountryRegionSelectedCb = useCallback(
		(regionId: number, countryId: number) =>
			isRegionSelected(regionId, countryId, selectedCountryRegions, selectedLocation),
		[selectedCountryRegions, selectedLocation],
	);

	const isStateSelectedCb = useCallback(
		(stateId: number, regionId: number, countryId: number) =>
			isStateSelected(
				stateId,
				regionId,
				countryId,
				selectedRegionStates,
				selectedCountryRegions,
				selectedLocation,
			),
		[selectedRegionStates, selectedCountryRegions, selectedLocation],
	);

	const isCitySelectedCb = useCallback(
		(cityId: number, stateId: number, regionId: number, countryId: number) =>
			isCitySelected(
				cityId,
				stateId,
				regionId,
				countryId,
				selectedStateCities,
				selectedRegionStates,
				selectedCountryRegions,
				selectedLocation,
			),
		[selectedStateCities, selectedRegionStates, selectedCountryRegions, selectedLocation],
	);

	// Action handlers
	const handleContinentChange = useCallback(
		(continent: TContinent) => {
			const ids = countryIdsByContinent(continent);

			let updatedIds: number[] = [];
			if (hasContinentSelected(continent)) {
				updatedIds = removeInputArrayIdentifiersFromAnotherArray(ids, selectedLocation);
			} else {
				updatedIds = removeDuplicatesFromArray([...selectedLocation, ...ids]);
			}

			handleBulkUpdate(updatedIds);
		},
		[countryIdsByContinent, handleBulkUpdate, hasContinentSelected, selectedLocation],
	);

	const toggleExpandItem = useCallback(
		(item: TContinent | TCountry | TRegion | TState) => {
			if (expanded[item.id] === undefined) {
				setExpanded({
					...expanded,
					[item.id]: true,
				});
			} else {
				let toBeUpdatedList = {...expanded};
				if (expanded[item.id]) {
					toBeUpdatedList = collapseDescendants(item, toBeUpdatedList);
				} else {
					toBeUpdatedList = {...toBeUpdatedList, [item.id]: true};
				}
				setExpanded(toBeUpdatedList);
			}
		},
		[expanded],
	);

	const toggleRegion = useCallback(
		(region: TRegion, country: TCountry) => {
			if (!handleRegionChange) return;
			handleRegionChange(region.id, country);
		},
		[handleRegionChange],
	);

	const toggleState = useCallback(
		(state: TState, region: TRegion, country: TCountry) => {
			if (!handleStateChange) return;
			handleStateChange(state.id, region, country);
		},
		[handleStateChange],
	);

	const toggleCity = useCallback(
		(city: TCity, state: TState, region: TRegion, country: TCountry) => {
			if (!handleCityChange) return;
			handleCityChange(city.id, state, region, country);
		},
		[handleCityChange],
	);

	return {
		// Data
		filteredData: filteredData as TContinent[],
		loading,
		expanded,

		// Derived state
		shouldRenderCountryRegions,
		shouldRenderTags,

		// Handlers
		onFilterChanged,
		handleContinentChange,
		toggleExpandItem,
		toggleRegion,
		toggleState,
		toggleCity,

		// Selection checks
		hasChildren,
		hasContinentSelected,
		isCountrySelectedCb,
		isCountryPartiallySelectedCb,
		isRegionPartiallySelectedCb,
		isStatePartiallySelectedCb,
		isCountryRegionSelectedCb,
		isStateSelectedCb,
		isCitySelectedCb,
		isContinentFullySelected,
		isCountryPartiallySelected,
	};
};
